import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import {
	Draggable,
	DraggableProvided,
	DraggableStateSnapshot,
	DraggingStyle,
	NotDraggingStyle,
} from 'react-beautiful-dnd';
import * as AdminModels from '../../../../models/AdminModels';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
} from '../../../../web/components-v2/Dropdown';
import { DragGripperIcon } from '../../../../web/components/svgs/icons/DragGripperIcon';
import { borderColor } from '../../../../web/styles/colors';
import { baseStyleSheet, bs } from '../../../../web/styles/styles';
import { useAccordionTriggerExpanded } from '../CategoriesByIndustry/AccordionDnD';
import { ChangeHWCCategoryModal } from './ChangeHWCCategoryModal';
import { useHwcTemplateActions } from './hooks/useHwcTemplateActions';
import { styleSheet } from './styles';

export function AccordionContentCategoryTemplateRow({
	template,
	industry,
	index,
	category,
}: {
	category: Api.ITemplateCategory;
	industry: Api.Industry;
	template: AdminModels.ISystemEmailTemplate<Api.ITemplate>;
	index: number;
}) {
	const isDraft = template.industries[industry] === Api.TemplateStatus.Draft;
	const industriesText = Object.keys(template.industries).join(', ').replace('_Others_', 'Others') ?? '';
	const createdText = template.template.creationDate
		? moment(template.template.creationDate).format('MM/DD/YYYY')
		: null;
	const [templateToChangeCategory, setTemplateToChangeCategory] = React.useState<string>();
	const isParentExpanded = useAccordionTriggerExpanded();
	const getItemStyle = (isDragging: boolean, draggableStyle: DraggingStyle | NotDraggingStyle): React.CSSProperties => {
		return {
			background: isDragging ? 'white' : '',
			borderBottom: isDragging ? `1px solid ${borderColor}` : '',
			borderRadius: isDragging ? '3px' : '',
			borderTop: isDragging ? `1px solid ${borderColor}` : '',
			...draggableStyle,
		};
	};
	const {
		handleDeleteTemplate,
		handleArchiveTemplate,
		handleFeatureTemplate,
		handleUnfeatureTemplate,
		handleViewTemplate,
		handleEditTemplate,
	} = useHwcTemplateActions({ industry });

	const handleChangeCategoryTemplate = () => {
		setTemplateToChangeCategory(template.template.id);
	};

	return (
		<>
			<Draggable
				key={template.template.id}
				disableInteractiveElementBlocking={false}
				draggableId={`${category.name}:${template.template.id}`}
				isDragDisabled={!isParentExpanded}
				index={index}
			>
				{(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
					return (
						<div
							className={css(styleSheet.containerTemplateRow)}
							ref={provided.innerRef}
							{...(provided?.draggableProps || {})}
							style={getItemStyle(snapshot.isDragging, provided.draggableProps?.style)}
						>
							<div {...(provided?.dragHandleProps || {})} className={css(styleSheet.gripper)}>
								<DragGripperIcon />
							</div>
							<div className={css(styleSheet.items)}>
								<div className={css(styleSheet.name, baseStyleSheet.truncateText)}>
									<div className={css(bs.textBrandPrimary, bs.textBase, bs.truncateText)}>{template.template.name}</div>
									{isDraft ? <span className={css(styleSheet.draft)}>DRAFT</span> : null}
								</div>
								<span className={css(styleSheet.industries)}>{industriesText}</span>
								<span className={css(styleSheet.created)}>{createdText}</span>
								<span className={css(styleSheet.actions)}>
									<Dropdown>
										<DropdownEllipsisTrigger />
										<DropdownContent>
											{category.type === Api.TemplateCategoryType.Featured ? (
												<>
													<DropdownItem onClick={() => handleEditTemplate({ template })}>Edit</DropdownItem>
													<DropdownItem onClick={() => handleUnfeatureTemplate({ template, category })}>
														Un-feature
													</DropdownItem>
												</>
											) : null}
											{category.type === Api.TemplateCategoryType.Archived ? (
												<>
													<DropdownItem onClick={handleChangeCategoryTemplate}>Restore</DropdownItem>
													<DropdownItem onClick={() => handleViewTemplate({ template })}>View</DropdownItem>
													<DropdownItem onClick={() => handleDeleteTemplate({ template })}>Delete</DropdownItem>
												</>
											) : null}
											{![Api.TemplateCategoryType.Featured, Api.TemplateCategoryType.Archived].includes(
												category.type
											) ? (
												<>
													<DropdownItem onClick={() => handleViewTemplate({ template })}>View</DropdownItem>
													<DropdownItem onClick={() => handleEditTemplate({ template })}>Edit</DropdownItem>
													<DropdownItem onClick={handleChangeCategoryTemplate}>Change Category</DropdownItem>
													<DropdownItem onClick={() => handleFeatureTemplate({ template })}>Feature</DropdownItem>
													<DropdownItem onClick={() => handleArchiveTemplate({ template })}>Archive</DropdownItem>
												</>
											) : null}
										</DropdownContent>
									</Dropdown>
								</span>
							</div>
						</div>
					);
				}}
			</Draggable>
			{templateToChangeCategory ? (
				<ChangeHWCCategoryModal
					templateId={templateToChangeCategory}
					categoryName={category.name}
					industry={industry}
					onRequestClose={() => setTemplateToChangeCategory(null)}
				/>
			) : null}
		</>
	);
}
